/**
 * Following the rows of the documents page that are on their way. An audio file is processed
 * from the moment it is uploaded, and twenty of them wait for hours behind one worker: reading
 * the whole folder tree again every few seconds for their sake loads every document of the case
 * each time. The page asks for the rows it follows instead, by id — their status and stage —
 * puts a new stage on the row it shows, and loads the tree again only once a status changed:
 * the tree's counts, filters and folders depend on the statuses, a stage is the row's alone.
 * Pure.
 */
import type { CaseDocumentProgress } from '@/services/api/cases'
import { isAudioInFlight } from './audio-stage'
import type { FolderTreeFile, FolderTreeResponse } from './types'

/** How often the followed rows are asked for. */
export const DOCUMENT_PROGRESS_POLL_MS = 5000

/** How many documents one question may name: the server refuses more. */
export const MAX_FOLLOWED_DOCUMENTS = 500

/**
 * The rows the page follows: every document that is being processed, and an audio file from the
 * queue on — its job starts with its upload, so its row is "analyzing" or "scheduled" before it
 * is "processing".
 */
export function followedDocumentIds(files: readonly FolderTreeFile[]): string[] {
  return files
    .filter(file => file.document.status === 'processing' || isAudioInFlight(file.document))
    .slice(0, MAX_FOLLOWED_DOCUMENTS)
    .map(file => file.document.id)
}

export interface HeldProgress {
  /** The tree with the stages the read says; the tree that was handed in when none moved. */
  tree: FolderTreeResponse
  /** A followed row changed its status, or is gone: the tree is to be loaded again. */
  reload: boolean
}

/**
 * Holds what was read of the rows `askedIds` against the tree the page shows. A row the page
 * shows but did not ask for is left alone; so is an answer for a row it no longer shows.
 */
export function holdProgressAgainst(
  tree: FolderTreeResponse,
  askedIds: readonly string[],
  progress: readonly CaseDocumentProgress[]
): HeldProgress {
  const asked = new Set(askedIds)
  const read = new Map(progress.map(row => [row.id, row]))
  let reload = false
  let moved = false
  const files = tree.files.map(file => {
    if (!asked.has(file.document.id)) return file
    const row = read.get(file.document.id)
    if (row === undefined || row.status !== file.document.status) {
      reload = true
      return file
    }
    // The tree of an older server leaves the two fields out: that is no stage, as null is.
    if (
      row.stage === (file.document.stage ?? null) &&
      row.failed_stage === (file.document.failed_stage ?? null)
    ) {
      return file
    }
    moved = true
    return {
      ...file,
      document: { ...file.document, stage: row.stage, failed_stage: row.failed_stage },
    }
  })
  return { tree: moved ? { ...tree, files } : tree, reload }
}
